import type { FC } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutgoingMail } from 'react-icons/md';
import ThreePOutlinedIcon from '@mui/icons-material/ThreePOutlined';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import SpeedometerChart from 'src/components/dashboard/charts/SpeedometerChart';
import ComparisonPill from 'src/components/dashboard/DashboardPage/components/ComparisonPill/ComparisonPill';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import WidgetLegend from 'src/components/dashboard/DashboardPage/widgets/SpeedometerWidget/WidgetLegend';
import type { Aggregate } from 'src/components/followUp/common/types';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { useExportOptions, useSpeedometerCalculations } from './hooks';

const SpeedometerWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const theme = useTheme();
  const upXl = useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true });
  const { t } = useTranslation('followUp');
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.RESPONSE_RATE,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, queryFilters, comparisonData } =
    useWidgetQuery<Aggregate>(endpoint);

  const {
    absoluteValue,
    percentage,
    comparisonPercentage,
    percentageDifference,
    absoluteDifference,
  } = useSpeedometerCalculations({
    data,
    comparisonData,
    isFetching,
    errorMsg,
  });

  const exportOptions = useExportOptions({
    data: data ?? null,
    comparisonData: comparisonData ?? null,
    comparisonPercentage,
    percentage,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      loading={isFetching}
      refetch={refetch}
      error={errorMsg}
      ariaId={ariaId}
      exportOptions={exportOptions}
      header={
        <Typography variant="h5" id={ariaId}>
          {config.name}
        </Typography>
      }
    >
      <Box
        sx={{
          p: 2,
          flexGrow: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ my: 'auto' }}>
          <SpeedometerChart
            percentage={percentage}
            height={280}
            color={theme.colors.secondary[500]}
          >
            <Typography
              component="span"
              sx={[
                {
                  fontSize: upXl ? '4.25rem' : '2.5rem',
                  fontWeight: 700,
                  lineHeight: 'normal',
                },
                { position: 'relative' },
                percentageDifference === undefined ? { mb: 0 } : { mb: 3 },
              ]}
            >
              {Math.round(absoluteValue)}
              <span style={{ marginLeft: 8 }}>%</span>
              {percentageDifference !== undefined && (
                <ComparisonPill
                  value={percentageDifference}
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                />
              )}
            </Typography>
            <Typography variant="body3" sx={{ fontWeight: 600 }}>
              {t('statistics.responseRate')}
            </Typography>
          </SpeedometerChart>
        </Box>
        <WidgetLegend
          labelValuePairs={[
            {
              label: (
                <>
                  <MdOutgoingMail size={18} />
                  {t('statistics.invitees')}
                </>
              ),
              value: data?.totalObjects,
              key: 'Invitees',
              comparisonValue: absoluteDifference?.totalObjects,
            },
            {
              label: (
                <>
                  <ThreePOutlinedIcon sx={{ fontSize: 18 }} />
                  {t('statistics.respondents')}
                </>
              ),
              value: data?.value,
              key: 'Respondents',
              comparisonValue: absoluteDifference?.value,
            },
          ]}
        />
      </Box>
    </WidgetFrame>
  );
};

export default SpeedometerWidget;
